import React, { ReactNode } from 'react';
import { usePermissions } from '@/hooks/usePermissions';

interface ConditionalRenderProps {
  children: ReactNode;
  fallback?: ReactNode;
  show?: boolean;
  permission?: string;
  resource?: string;
  action?: string;
  role?: 'admin' | 'representante' | 'parceiro';
  anyOf?: Array<'admin' | 'representante' | 'parceiro'>;
  allOf?: Array<'admin' | 'representante' | 'parceiro'>;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  children,
  fallback = null,
  show,
  permission,
  resource,
  action,
  role,
  anyOf,
  allOf,
}) => {
  const permissions = usePermissions();

  // Verificação direta de show
  if (show !== undefined && !show) {
    return <>{fallback}</>;
  }

  // Verificação de permissão específica
  if (permission && !permissions.can(permission)) {
    return <>{fallback}</>;
  }

  // Verificação de acesso a recurso
  if (resource && action && !permissions.canAccess(resource, action)) {
    return <>{fallback}</>;
  }

  // Verificação de papel específico
  if (role && !permissions.hasRole(role)) {
    return <>{fallback}</>;
  }

  // Verificação de qualquer um dos papéis (OR)
  if (anyOf && anyOf.length > 0) {
    const hasAnyRole = anyOf.some(r => permissions.hasRole(r));
    if (!hasAnyRole) {
      return <>{fallback}</>;
    }
  }

  // Verificação de todos os papéis (AND)
  if (allOf && allOf.length > 0) {
    const hasAllRoles = allOf.every(r => permissions.hasRole(r));
    if (!hasAllRoles) {
      return <>{fallback}</>;
    }
  }

  // Se todas as verificações passaram, renderizar o conteúdo
  return <>{children}</>;
};

// Componentes de conveniência para casos comuns
export const IfAdmin: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender role="admin" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfRepresentative: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender role="representante" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfPartner: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender role="parceiro" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfAdminOrRepresentative: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender anyOf={['admin', 'representante']} fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanManageLeads: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="leads:manage" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanReadClients: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="clients:read" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanCreateProposals: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="proposals:create" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanViewReports: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="reports:read" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanManageUsers: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="users:manage" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanAccessSettings: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="settings:read" fallback={fallback}>
    {children}
  </ConditionalRender>
);

export const IfCanManageDimensioning: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ConditionalRender permission="dimensioning:manage" fallback={fallback}>
    {children}
  </ConditionalRender>
);

